import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdwDialogModule } from '@cdw-ae/common-ui-components-dialog';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import { CommonUiComponentsSpinnerComponent } from '@cdw-ae/common-ui-components-spinner';
import { CommonUiComponentsTextInputModule } from '@cdw-ae/common-ui-components-text-input';

export interface OuGroup {
  ouPath: string;
  deviceCount: number;
  deviceIds: string[];
}

export interface MoveToLastUserOuDialogData {
  deviceIds: string[];
  /**
   * EDUS-2486 Hit-list #6b: when launched from the SSRM-mode navbar
   * action, the client doesn't have the per-device id list (selection
   * lives in Redis on the server). Pass the count separately so the
   * dialog can render "X devices selected" while the preview loads.
   * Falls back to `deviceIds.length` when omitted.
   */
  totalDeviceCount?: number;
  deviceToOuMapping?: Array<{ deviceId: string; currentOrgUnitPath: string; orgUnitPath: string }>;
}

@Component({
  selector: 'app-move-to-last-user-ou-dialog',
  templateUrl: './move-to-last-user-ou-dialog.component.html',
  styleUrls: ['./move-to-last-user-ou-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    CdwDialogModule,
    CdwIconModule,
    CommonUiComponentsSpinnerComponent,
    CommonUiComponentsTextInputModule,
  ],
  standalone: true,
})
export class MoveToLastUserOuDialogComponent implements OnInit {
  public ouGroups: OuGroup[] = [];
  public filteredOuGroups: OuGroup[] = [];
  public totalDeviceCount = 0;
  public devicesWithValidOu = 0;
  public devicesWithoutOu = 0;
  public devicesAlreadyInTargetOu = 0;
  public searchTerm = '';
  public isLoading = true;
  public hasError = false;
  public currentStep: 'preview' | 'confirm' = 'preview';
  private deviceIdsToMove: string[] = []; // Device IDs that actually need to be moved

  constructor(
    public dialogRef: MatDialogRef<MoveToLastUserOuDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MoveToLastUserOuDialogData
  ) {
    this.totalDeviceCount = data.totalDeviceCount ?? data.deviceIds.length;
  }

  ngOnInit(): void {
    // If data is already provided, process it immediately
    if (this.data.deviceToOuMapping) {
      this.processOuMapping(this.data.deviceToOuMapping);
    }
  }

  public updateWithOuMapping(deviceToOuMapping: Array<{ deviceId: string; currentOrgUnitPath: string; orgUnitPath: string }> | null): void {
    if (!deviceToOuMapping || deviceToOuMapping.length === 0) {
      this.hasError = true;
      this.isLoading = false;
      return;
    }

    this.processOuMapping(deviceToOuMapping);
  }

  private processOuMapping(deviceToOuMapping: Array<{ deviceId: string; currentOrgUnitPath: string; orgUnitPath: string }>): void {
    // Separate devices into categories:
    // 1. Already in target OU (currentOrgUnitPath === orgUnitPath) - FILTER OUT
    // 2. Valid OUs that need moving
    // 3. No target OU available
    const validOuMap = new Map<string, string[]>();
    const devicesWithoutOuList: string[] = [];
    let alreadyInTargetOuCount = 0;

    for (const mapping of deviceToOuMapping) {
      const currentOu = mapping.currentOrgUnitPath;
      const targetOu = mapping.orgUnitPath;

      // Skip devices that are already in their target OU
      if (currentOu && targetOu && currentOu === targetOu) {
        alreadyInTargetOuCount++;
        continue;
      }

      // Check if target OU path is null, undefined, empty, or just whitespace
      if (!targetOu || targetOu.trim() === '') {
        devicesWithoutOuList.push(mapping.deviceId);
      } else {
        if (!validOuMap.has(targetOu)) {
          validOuMap.set(targetOu, []);
        }
        validOuMap.get(targetOu)?.push(mapping.deviceId);
      }
    }

    // Convert map to array of OuGroup objects for valid OUs
    const validOuGroups = Array.from(validOuMap.entries())
      .map(([ouPath, deviceIds]) => ({
        ouPath,
        deviceCount: deviceIds.length,
        deviceIds,
      }))
      .sort((a, b) => b.deviceCount - a.deviceCount); // Sort by device count descending

    // Add "Unknown OU" group at the end if there are devices without OUs
    if (devicesWithoutOuList.length > 0) {
      validOuGroups.push({
        ouPath: 'Unknown (No OU assigned)',
        deviceCount: devicesWithoutOuList.length,
        deviceIds: devicesWithoutOuList,
      });
    }

    // Extract all device IDs that will actually be moved (exclude "Unknown OU" devices)
    this.deviceIdsToMove = Array.from(validOuMap.values()).flat();

    this.ouGroups = validOuGroups;
    this.filteredOuGroups = [...this.ouGroups];
    this.devicesWithValidOu = this.deviceIdsToMove.length;
    this.devicesWithoutOu = devicesWithoutOuList.length;
    this.devicesAlreadyInTargetOu = alreadyInTargetOuCount;
    this.isLoading = false;
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;

    if (!value || value.trim() === '') {
      this.filteredOuGroups = [...this.ouGroups];
      return;
    }

    const lowerSearchTerm = value.toLowerCase();
    this.filteredOuGroups = this.ouGroups.filter(group =>
      group.ouPath.toLowerCase().includes(lowerSearchTerm)
    );
  }

  public onNext(): void {
    this.currentStep = 'confirm';
  }

  public onPrevious(): void {
    this.currentStep = 'preview';
  }

  public onConfirm(): void {
    this.dialogRef.close({
      confirmed: true,
      deviceIdsToMove: this.deviceIdsToMove
    });
  }

  public onCancel(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
